import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/primitives";
import { Calendar, HelpCircle, Bookmark } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { IntroStep } from "../components";
import { useCheckInData } from "../hooks";
import { createBookmark } from "../utils";
import { ReflectionGoalDialog } from "@/components/ReflectionGoalDialog";
import type { ReflectionGoal } from "@/lib/reflection-progress";

export function CheckInIntroPage() {
	const { user, profile, updateProfile } = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const { totalCheckInsCount, loading } = useCheckInData(user?.id);
	const [showGoalDialog, setShowGoalDialog] = useState(false);
	const [goalDialogSkippedThisSession, setGoalDialogSkippedThisSession] =
		useState(false);

	// Skip intro if requested and go directly to form
	useEffect(() => {
		if (!loading && searchParams.get("skipIntro") === "true") {
			navigate("/daily-checkin/form", { replace: true });
		}
	}, [loading, searchParams, navigate]);

	const handleStartCheckIn = () => {
		// Check if user has set a goal before starting check-in
		// Only show if they haven't skipped it this session
		if (!profile?.reflection_goal && !goalDialogSkippedThisSession) {
			setShowGoalDialog(true);
			return;
		}

		// Proceed to check-in form
		navigate("/daily-checkin/form");
	};

	const handleGoalSet = async (goal: ReflectionGoal) => {
		setShowGoalDialog(false);
		if (updateProfile) {
			await updateProfile({
				reflection_goal: goal,
				reflection_goal_set_at: new Date().toISOString(),
			});
		}

		// After setting goal, proceed to check-in
		navigate("/daily-checkin/form");
	};

	const handleSkipGoal = () => {
		setShowGoalDialog(false);
		setGoalDialogSkippedThisSession(true);
		// Proceed to check-in without setting a goal
		navigate("/daily-checkin/form");
	};

	if (!user || loading) {
		return (
			<div className="container mx-auto px-4 py-8">
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
			<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
				{/* Header with actions */}
				<div className="mb-6 sm:mb-8">
					<div className="flex justify-end gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => navigate("/daily-checkin/welcome")}
							className="flex-shrink-0"
						>
							<HelpCircle className="h-4 w-4" />
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => navigate("/check-ins")}
							className="flex-shrink-0"
						>
							<Calendar className="h-4 w-4 mr-2" />
							<span className="hidden sm:inline">View All</span>
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={createBookmark}
							title="Create bookmark"
							className="flex-shrink-0"
						>
							<Bookmark className="h-4 w-4 sm:mr-2" />
							<span className="hidden sm:inline">Bookmark</span>
						</Button>
					</div>
				</div>

				<IntroStep onStartCheckIn={handleStartCheckIn} />

				{user && (
					<ReflectionGoalDialog
						open={showGoalDialog}
						onGoalSet={handleGoalSet}
						onSkip={handleSkipGoal}
						userId={user.id}
					/>
				)}
			</div>
		</div>
	);
}
